import { FC, useCallback, useEffect, useMemo, useState } from "react";
import HorizontalPadding from "../components/HorizontalPadding";
import InventoryManifestForm from "./Inventory/InventoryForm";
import InventoryDetailForm from "./Inventory/InventoryDetailForm";
import InventoryDetailTable from "./Inventory/InventoryDetailTable";
import { useAppInventory, useAppSelector } from "../store/hooks";
import { PrimaryButton, SecondaryButton } from "../components/Buttons";
import {
  addPendingDetails,
  addProcessedDetails,
  clearInventoryValues,
  removePendingDetails,
  removeProcessedDetails,
  setInventoryLoadFromDB,
} from "../store/slices/inventory";
import {
  InventoryDetailSingleInterface,
  StatusInventory,
} from "../interfaces/InventoryInterface";
import {
  ManifestIncidence,
} from "../interfaces/ManifestInterface";
import { createInventoryDetail, createObjectInventoryDetail, generateInventory, updateInventoryStatus, validationInventoryDetail } from "../services/inventoryServices";
import { alertService, loaderService } from "../services";
import { useNavigate } from "react-router-dom";
import Modal from "../components/Modal";
import {
  CheckCircleIcon,
  ExclamationTriangleIcon,
  QuestionMarkCircleIcon,
} from "@heroicons/react/24/outline";
import InventoryInvalidDetailTable from "./Inventory/InventoryInvalidDetailTable";
import InventoryForm from "./Inventory/InventoryForm";

const InventoryCreate: FC = () => {
  const navigate = useNavigate();
  const inventory = useAppInventory();
  const loading = loaderService.useIsLoading();
  const user = useAppSelector((state) => state.user);
  const loadFromDB = useAppSelector((state) => state.inventory.loadFromDB);
  const inventoryHeader = useAppSelector(
    (state) => state.inventory.inventoryHeader
  );
  const pendingDetails = useAppSelector(
    (state) => state.inventory.pendingDetails
  );
  const processedDetails = useAppSelector(
    (state) => state.inventory.processedDetails
  );

  const [openErrorModal, setOpenErrorModal] = useState(false);
  const [openFinishModal, setOpenFinishModal] = useState(false);
  const [openCleanWarning, setOpenCleanWarning] = useState(false);
  const [openSuccessModal, setOpenSuccessModal] = useState(false);
  const [openFinishWarningModal, setOpenFinishWarningModal] = useState(false);
  const [detailsBeingValidated, setDetailsBeingValidated] = useState<string[]>(
    []
  );
  const [openContinueLaterWarning, setOpenContinueLaterWarning] =
    useState(false);

  const processedDetailList = useMemo(() => {
    return Object.keys(processedDetails).map((id) => processedDetails[id]);
  }, [processedDetails]);

  const pendingDetailList = useMemo(() => {
    return Object.keys(pendingDetails).map((id) => pendingDetails[id]);
  }, [pendingDetails]);

  const incompleteShipments = useMemo(() => {
    const incompleteShipments: { [shipmentNumber: string]: Set<number> } = {};
    const result: { [shipmentNumber: string]: Set<number> } = {};

    processedDetailList.forEach((detail) => {
      const shipmentNumber = detail.shipmentNumber;
      const totalPieces = detail.totalPieces;
      incompleteShipments[shipmentNumber] = new Set(
        new Array(totalPieces).fill(null).map((_, i) => i + 1)
      );
    });

    processedDetailList.forEach((detail) => {
      const shipmentNumber = detail.shipmentNumber;
      const pieceNumber = detail.pieceNumber;

      incompleteShipments[shipmentNumber].delete(pieceNumber);
    });

    Object.keys(incompleteShipments).forEach((shipmentNumber) => {
      if (incompleteShipments[shipmentNumber].size > 0) {
        result[shipmentNumber] = incompleteShipments[shipmentNumber];
      }
    });

    return result;
  }, [processedDetailList]);

  const totalPieces = useMemo(() => {
    const result: { [shipmentNumber: string]: number } = {};

    processedDetailList.forEach((detail) => {
      const shipmentNumber = detail.shipmentNumber;
      const totalPieces = detail.totalPieces;
      result[shipmentNumber] = totalPieces;
    });

    return result;
  }, [processedDetailList]);

  const addDetailsBeingValidated = (
    details: InventoryDetailSingleInterface[]
  ) => {
    setDetailsBeingValidated((oldDetails) =>
      oldDetails.concat(
        details.map((d) => `${d.shipmentNumber}-${d.pieceNumber}`)
      )
    );
  };

  const removeDetailsBeingValidated = (
    details: InventoryDetailSingleInterface[]
  ) => {
    setDetailsBeingValidated((oldDetails) =>
      oldDetails.filter(
        (o) =>
          !details
            .map((d) => `${d.shipmentNumber}-${d.pieceNumber}`)
            .some((d) => d === o)
      )
    );
  };

  const processDetails = useCallback(
    async (details: InventoryDetailSingleInterface[]) => {
      const userLogin = user.user?.userLogin;
      const buCode = user.businessUnit?.code;
      const inventoryHeaderID = inventoryHeader?.inventoryHeaderID;

      if (!userLogin) {
        alertService.error(
          "No hay un nombre de usuario asociado a la sesión actual"
        );
        return;
      }
      if (!buCode) {
        alertService.error(
          "No hay una tienda asociada a la sesión del usuario"
        );
        return;
      }
      if (!inventoryHeaderID) {
        alertService.error(
          "Se necesita crear el inventario antes de agregar piezas"
        );
        return;
      }

      // Validate those details again
      addDetailsBeingValidated(details);
      const validations = await validationInventoryDetail(
        details,
        buCode
      );

      if (
        validations.didError ||
        !validations.model ||
        validations.model.length === 0
      ) {
        alertService.error(
          "Hubo un error validando la pieza",
          validations.errorMessage
        );
        removeDetailsBeingValidated(details);
        return;
      }

      const validatedDetails = details.map((d, i) => ({
        ...d,
        incidence: validations.model![i].incidence,
        errors: validations.model![i].errors,
      }));

      // Re-add invalid details
      const newInvalidDetails = validatedDetails.filter(
        (d) => d.incidence !== ManifestIncidence.NO_ERROR
      );

      inventory(removePendingDetails(newInvalidDetails));
      inventory(addProcessedDetails(newInvalidDetails));

      // Try to add the successful details to the manifest
      const newValidDetails = validatedDetails.filter(
        (d) => d.incidence === ManifestIncidence.NO_ERROR
      );
      if (newValidDetails.length === 0) {
        removeDetailsBeingValidated(details);
        return;
      }

      const manifestDetails = createObjectInventoryDetail(
        newValidDetails,
        inventoryHeaderID,
        userLogin,
        buCode,
        false
      );
      const filteredManifestDetails = manifestDetails.filter(
        (d) => !d.inventoryDetailID
      );
      const insertedDetail = await createInventoryDetail(
        filteredManifestDetails
      );

      if (insertedDetail.didError || insertedDetail.model === null) {
        alertService.error(
          "Hubo un error insertando las piezas al inventario",
          insertedDetail.errorMessage
        );
        const newInvalidDetails = newValidDetails.map((d) => ({
          ...d,
          incidence: ManifestIncidence.ERROR,
          errors: insertedDetail.errorMessage,
        }));
        removeDetailsBeingValidated(details);
        inventory(removePendingDetails(newInvalidDetails));
        inventory(addProcessedDetails(newInvalidDetails));
        return;
      }

      let index = 0;
      const completeDetails = newValidDetails.map((d) => ({
        ...d,
        inventoryDetailID:
          d.inventoryDetailID ??
          insertedDetail.model![index++].inventoryDetailID,
      }));
      removeDetailsBeingValidated(details);
      inventory(removePendingDetails(completeDetails));
      inventory(addProcessedDetails(completeDetails));

      return completeDetails;
    },
    [inventory, inventoryHeader, user]
  );

  const reProcessDetails = async (
    detailList: InventoryDetailSingleInterface[]
  ) => {
    const invalidDetails = detailList.filter(
      (d) =>
        d.incidence === ManifestIncidence.CRITIC_ERROR ||
        d.incidence === ManifestIncidence.NOT_VALIDATED
    );

    if (invalidDetails.length === 0) {
      return;
    }

    // Pass invalid details from processed to pending
    inventory(removeProcessedDetails(invalidDetails));
    inventory(
      addPendingDetails(
        invalidDetails.map((d) => ({
          ...d,
          incidence: ManifestIncidence.NOT_VALIDATED,
          errors: "",
        }))
      )
    );

    return await processDetails(invalidDetails);
  };

  const reProcessAllDetails = async () => {
    await reProcessDetails(
      processedDetailList
        .concat(pendingDetailList)
        .filter(
          (d) =>
            !detailsBeingValidated.some(
              (v) => v === `${d.shipmentNumber}-${d.pieceNumber}`
            )
        )
    );
  };

  const handleContinueLater = async () => {
    if (pendingDetailList.length !== 0) {
      alertService.error(
        "Todavía hay piezas procesándose, espere un momento..."
      );
      return;
    }

    const invalidDetails = processedDetailList.filter(
      (d) => d.incidence !== ManifestIncidence.NO_ERROR
    );
    if (invalidDetails.length === 0) {
      navigate("/inventory");
      return;
    }

    const newProcessedDetails = await reProcessDetails(invalidDetails);

    if (!newProcessedDetails) {
      setOpenContinueLaterWarning(true);
      return;
    }

    const newInvalidDetails = newProcessedDetails.filter(
      (d) => d.incidence !== ManifestIncidence.NO_ERROR
    );
    if (newInvalidDetails.length === 0) {
      navigate("/inventory");
      return;
    }

    setOpenContinueLaterWarning(true);
  };

  const handleOnFinish = () => {
    if (pendingDetailList.length !== 0) {
      alertService.error(
        "Todavía hay piezas procesándose, espere un momento..."
      );
      return;
    }

    if (processedDetailList.length === 0) {
      alertService.error("No se puede guardar un inventario sin detalles");
      return;
    }

    // const invalidDetails = processedDetailList.filter(
    //   (d) => d.incidence !== ManifestIncidence.NO_ERROR
    // );

    // if (invalidDetails.length !== 0) {
    //   setOpenErrorModal(true);
    //   return;
    // }

    if (Object.keys(incompleteShipments).length > 0) {
      setOpenFinishWarningModal(true);
      return;
    }

    setOpenFinishModal(true);
  };

  const handleClean = async () => {
    const userLogin = user.user?.userLogin;
    const inventoryHeaderID = inventoryHeader?.inventoryHeaderID;

    if (!inventoryHeaderID) {
      inventory(clearInventoryValues());
      return;
    }
    if (!userLogin) {
      alertService.error(
        "No hay un nombre de usuario asociado a la sesión actual"
      );
      return;
    }

    const response = await updateInventoryStatus(
      inventoryHeaderID,
      StatusInventory.DISCARDED,
      userLogin
    );

    if (response.didError) {
      alertService.error(
        "Hubo un error Descartando el inventario",
        response.errorMessage
      );
      return;
    }

    inventory(clearInventoryValues());
  };

  const finish = async () => {
    const userLogin = user.user?.userLogin;
    const inventoryHeaderID = inventoryHeader?.inventoryHeaderID;

    if (!userLogin) {
      alertService.error(
        "No hay un nombre de usuario asociado a la sesión actual"
      );
      return;
    }
    if (!inventoryHeaderID) {
      alertService.error(
        "Parece que no hay ningún inventario cargado"
      );
      return;
    }

    const result = await generateInventory(
      inventoryHeaderID,
      userLogin,
      inventoryHeader?.observation ?? ""
    );
    if (result.didError) {
      alertService.error(
        "Hubo un error cerrando el inventario",
        result.errorMessage
      );
      return;
    }

    setOpenSuccessModal(true);
  };

  useEffect(() => {
    if (loadFromDB) {
      inventory(setInventoryLoadFromDB(false));
      if (pendingDetailList.length > 0) {
        processDetails(pendingDetailList);
      }
    }
  }, [loadFromDB, pendingDetailList, processDetails, inventory]);

  return (
    <main className="lg:pl-72 pb-32">
      <div className="py-6 sm:px-6 lg:px-8 bg-white relative flex items-center justify-between h-32">
        <header className="ml-4 text-2xl font-bold text-gray-700 ">
          Crear Inventario
        </header>
      </div>

      <HorizontalPadding paddingTop>
        <div className="flex flex-col gap-6">
          <InventoryForm />

          {inventoryHeader && (
            <>
              <InventoryDetailForm
                addDetailsBeingValidated={addDetailsBeingValidated}
                removeDetailsBeingValidated={removeDetailsBeingValidated}
              />

              <InventoryDetailTable
                detailsBeingValidated={detailsBeingValidated}
                onRetry={reProcessAllDetails}
                onRetryDetail={async (detail) => {
                  await reProcessDetails([detail]);
                }}
              />
            </>
          )}

          <div className="flex justify-between">
            {inventoryHeader?.statusID === StatusInventory.PROCESS ? (
              <>
                <div className="flex gap-6 items-center">
                  <SecondaryButton
                    disabled={loading || detailsBeingValidated.length > 0}
                    onClick={() => setOpenCleanWarning(true)}
                  >
                    Limpiar todo
                  </SecondaryButton>
                </div>

                <div className="flex gap-6 items-center">
                  <SecondaryButton
                    disabled={loading || detailsBeingValidated.length > 0}
                    onClick={handleContinueLater}
                  >
                    Continuar luego
                  </SecondaryButton>

                  <PrimaryButton
                    disabled={loading || detailsBeingValidated.length > 0}
                    onClick={handleOnFinish}
                  >
                    Finalizar y cerrar
                  </PrimaryButton>
                </div>
              </>
            ) : (
              <div className="flex justify-end">
                <SecondaryButton onClick={() => navigate("/inventory")}>
                  Regresar
                </SecondaryButton>
              </div>
            )}
          </div>
        </div>
      </HorizontalPadding>

      <Modal
        openModal={openContinueLaterWarning}
        setOpenModal={setOpenContinueLaterWarning}
      >
        <div className="flex flex-col gap-4 items-center">
          <div className="flex flex-col items-center">
            <ExclamationTriangleIcon className="text-orange-500 h-28 w-28 mx-auto opacity-90" />
            <h2 className="text-xl font-semibold leading-7 text-gray-700 text-center mt-2">
              Continuar luego
            </h2>
          </div>

          <div className="flex flex-col gap-1 max-w-[32rem]">
            <p className="text-gray-600 text-center whitespace-pre-line">
              Las siguientes piezas presentan un error de validación y si
              quieres guardar para continuar luego, estas no serán incluidas en
              el borrador:
            </p>
          </div>

          <InventoryInvalidDetailTable details={processedDetailList} />

          <div className="flex w-full flex-row gap-3 items-center justify-between mt-4">
            <SecondaryButton
              onClick={() => setOpenContinueLaterWarning(false)}
              className="px-4 py-2"
            >
              Cancelar
            </SecondaryButton>

            <PrimaryButton
              onClick={() => navigate("/inventory")}
              className="px-4 py-2"
            >
              Aceptar
            </PrimaryButton>
          </div>
        </div>
      </Modal>

      <Modal openModal={openFinishModal} setOpenModal={setOpenFinishModal}>
        <div className="flex flex-col gap-4 max-w-md mx-auto items-center">
          <div className="flex flex-col items-center">
            <QuestionMarkCircleIcon className="text-blue-500 h-28 w-28 mx-auto opacity-90" />
            <h2 className="text-xl font-semibold leading-7 text-gray-700 text-center mt-2">
              Creación de inventario
            </h2>
          </div>

          <div className="flex flex-col gap-1">
            <p className="text-gray-600 text-center whitespace-pre-line">
              ¿Está seguro de finalizar y cerrar el inventario?
            </p>
          </div>

          <div className="flex w-full flex-row gap-3 items-center justify-between mt-4">
            <SecondaryButton
              onClick={() => setOpenFinishModal(false)}
              className="px-4 py-2"
            >
              Cancelar
            </SecondaryButton>

            <PrimaryButton
              disabled={loading}
              onClick={async () => {
                loaderService.start();
                await finish();
                loaderService.stop();
                setOpenFinishModal(false);
              }}
              className="px-4 py-2"
            >
              Aceptar
            </PrimaryButton>
          </div>
        </div>
      </Modal>

      <Modal
        openModal={openSuccessModal}
        setOpenModal={(open) => {
          inventory(clearInventoryValues());
          setOpenSuccessModal(open);
          navigate("/inventory");
        }}
      >
        <div className="flex flex-col gap-4 max-w-md mx-auto items-center">
          <div className="flex flex-col items-center">
            <CheckCircleIcon className="text-green-500 h-28 w-28 mx-auto opacity-90" />
            <h2 className="text-xl font-semibold leading-7 text-gray-700 text-center mt-2">
              Creación de inventario
            </h2>
          </div>

          <div className="flex flex-col gap-1">
            <p className="text-gray-600 text-center whitespace-pre-line">
              El inventario '{inventoryHeader?.inventoryHeaderCode}' ha sido
              generado exitosamente
            </p>
          </div>

          <div className="flex w-full flex-row gap-3 items-center justify-center mt-4">
            <PrimaryButton
              onClick={() => {
                inventory(clearInventoryValues());
                setOpenSuccessModal(false);
                navigate("/inventory");
              }}
              className="px-4 py-2"
            >
              Aceptar
            </PrimaryButton>
          </div>
        </div>
      </Modal>

      <Modal openModal={openCleanWarning} setOpenModal={setOpenCleanWarning}>
        <div className="flex flex-col gap-4 max-w-md mx-auto items-center">
          <div className="flex flex-col items-center">
            <QuestionMarkCircleIcon className="text-blue-500 h-28 w-28 mx-auto opacity-90" />
            <h2 className="text-xl font-semibold leading-7 text-gray-700 text-center mt-2">
              Borrar datos del inventario
            </h2>
          </div>

          <div className="flex flex-col gap-1">
            <p className="text-gray-600 text-center whitespace-pre-line">
              ¿Estás seguro de querer borrar los datos del inventario? Esta
              acción es irreversible
            </p>
          </div>

          <div className="flex w-full flex-row gap-3 items-center justify-between mt-4">
            <SecondaryButton
              onClick={() => setOpenCleanWarning(false)}
              className="px-4 py-2"
            >
              Cancelar
            </SecondaryButton>

            <PrimaryButton
              onClick={async () => {
                await handleClean();
                setOpenCleanWarning(false);
              }}
              className="px-4 py-2"
            >
              Aceptar
            </PrimaryButton>
          </div>
        </div>
      </Modal>

      <Modal
        openModal={openFinishWarningModal}
        setOpenModal={setOpenFinishWarningModal}
      >
        <div className="flex flex-col gap-4 items-center">
          <div className="flex flex-col items-center">
            <ExclamationTriangleIcon className="text-orange-500 h-28 w-28 mx-auto opacity-90" />
            <h2 className="text-xl font-semibold leading-7 text-gray-700 text-center mt-2">
              Creación de inventario
            </h2>
          </div>

          <div className="flex justify-center flex-1 flex-col items-center">
            <div className="flex flex-col gap-1 max-w-[28rem]">
              <p className="text-gray-600 text-center whitespace-pre-line">
                Las siguientes guías no tienen todas sus piezas completas:
              </p>
            </div>

            <div className="flex flex-col text-gray-700">
              {Object.keys(incompleteShipments).map((shipmentNumber) => (
                <p key={shipmentNumber} className="">
                  Guía <span className="font-semibold">{shipmentNumber}</span>:{" "}
                  <span className="font-semibold">
                    {Array.from(incompleteShipments[shipmentNumber])
                      .map((piece) => `${piece}/${totalPieces[shipmentNumber]}`)
                      .join(", ")}
                  </span>
                </p>
              ))}
            </div>
          </div>

          <div className="flex w-full flex-row gap-3 items-center justify-between mt-4">
            <SecondaryButton
              onClick={() => setOpenFinishWarningModal(false)}
              className="px-4 py-2"
            >
              Cancelar
            </SecondaryButton>

            <PrimaryButton
              disabled={loading}
              onClick={async () => {
                loaderService.start();
                await finish();
                loaderService.stop();
                setOpenFinishWarningModal(false);
                setOpenFinishModal(false);
              }}
              className="px-4 py-2"
            >
              Aceptar
            </PrimaryButton>
          </div>
        </div>
      </Modal>

      <Modal openModal={openErrorModal} setOpenModal={setOpenErrorModal}>
        <div className="flex flex-col gap-4 items-center">
          <div className="flex flex-col items-center">
            <ExclamationTriangleIcon className="text-orange-500 h-28 w-28 mx-auto opacity-90" />
            <h2 className="text-xl font-semibold leading-7 text-gray-700 text-center mt-2">
              Creación de inventario
            </h2>
          </div>

          <div className="flex flex-col gap-1 max-w-[20rem]">
            <p className="text-gray-600 text-center whitespace-pre-line">
              Aún hay piezas inválidas. Retírelas antes de finalizar el
              inventario
            </p>
          </div>

          <InventoryInvalidDetailTable details={processedDetailList} />

          <div className="flex w-full flex-row gap-3 items-center justify-center mt-4">
            <PrimaryButton
              onClick={() => setOpenErrorModal(false)}
              className="px-4 py-2"
            >
              Aceptar
            </PrimaryButton>
          </div>
        </div>
      </Modal>
    </main>
  );
};

export default InventoryCreate;
